<template>
    <div>
        <el-table
            ref="branded-caller-id-bundle-table"
            v-loading="loadingBundles"
            :data="bundles"
            class="w-full"
            stripe
        >
            <!-- Information Column -->
            <el-table-column
                prop="sid"
                label="Information"
                width="320"
            >
                <template v-slot="scope">
                    <InformationColumn
                        :row="scope.row"
                        :show-customer-profile="true"
                        :show-voice-integrity-bundles="true"
                    />
                </template>
            </el-table-column>

            <!-- Timestamps Column -->
            <el-table-column
                label="Timestamps"
                width="140"
            >
                <template v-slot="scope">
                    <TimestampsColumn :row="scope.row" />
                </template>
            </el-table-column>

            <!-- Phone Numbers Column -->
            <el-table-column
                label="Phone Numbers"
                prop="branded_caller_id_incoming_numbers_count"
                width="150"
            >
                <template v-slot="scope">
                    <CountColumn
                        :value="scope.row.branded_caller_id_incoming_numbers_count"
                        :single-value="true"
                    />
                </template>
            </el-table-column>

            <!-- Type Column -->
            <el-table-column
                label="Type"
                width="120"
            >
                <template v-slot="scope">
                    <div v-if="scope.row.compliance_registration">
                        <span
                            :class="scope.row.compliance_registration.type === TYPE_ENHANCED ? 'badge-enhanced' : 'badge-standard'"
                            class="type-badge"
                        >
                            {{ scope.row.compliance_registration.type === TYPE_ENHANCED ? 'Enhanced' : 'Standard' }}
                        </span>
                    </div>
                    <span v-else class="text-muted">-</span>
                </template>
            </el-table-column>

            <!-- Attributes Column -->
            <el-table-column
                label="Attributes"
                width="280"
            >
                <template v-slot="scope">
                    <div v-if="scope.row.compliance_registration">
                        <p class="mb-0"><span class="font-weight-bold">Display Name:</span></p>
                        <p class="mb-1">{{ scope.row.branded_calls_information && scope.row.branded_calls_information.bundle_attributes.branded_calls_display_name || '-' }}</p>

                        <template v-if="scope.row.compliance_registration.type === TYPE_ENHANCED">
                            <template v-if="scope.row.bcid_use_case || scope.row.branded_calls_information">
                                <p v-if="scope.row.bcid_use_case" class="mb-0">
                                    <span class="font-weight-bold">Use Case:</span>
                                </p>
                                <p v-if="scope.row.bcid_use_case" class="mb-1">
                                    {{ useCaseCategoryLabel(scope.row.bcid_use_case.bundle_attributes.category) }}
                                </p>
                                <p v-if="scope.row.branded_calls_information && scope.row.branded_calls_information.bundle_attributes.branded_calls_call_reason" class="mb-0">
                                    <span class="font-weight-bold">Call Reason:</span>
                                </p>
                                <p v-if="scope.row.branded_calls_information && scope.row.branded_calls_information.bundle_attributes.branded_calls_call_reason" class="mb-0">
                                    {{ scope.row.branded_calls_information.bundle_attributes.branded_calls_call_reason }}
                                </p>
                            </template>
                        </template>
                        <template v-else>
                            <p class="mb-0"><span class="font-weight-bold">Display Long Name:</span></p>
                            <p class="mb-0">{{ scope.row.branded_calls_information && scope.row.branded_calls_information.bundle_attributes.branded_calls_long_display_name || '-' }}</p>
                        </template>
                    </div>
                    <span v-else class="text-muted">No data</span>
                </template>
            </el-table-column>

            <!-- Status Column -->
            <el-table-column
                label="Status"
                prop="status_name"
                width="120"
            >
                <template v-slot="scope">
                    <StatusColumn :row="scope.row" />
                </template>
            </el-table-column>

            <!-- Failure Reason Column -->
            <el-table-column
                label="Failure reason"
                prop="failure_reason"
            >
                <template v-slot="scope">
                    <FailureReasonColumn :row="scope.row" />
                </template>
            </el-table-column>

            <!-- Actions Column -->
            <el-table-column
                label="Action"
                width="165"
            >
                <template v-slot="scope">
                    <!-- View Details Button -->
                    <div>
                        <el-button
                            type="primary"
                            size="mini"
                            class="btn-block"
                            @click="handleViewDetails(scope.row)"
                        >
                            View Details
                        </el-button>
                    </div>
                    <!-- Assign Numbers Button -->
                    <div class="mt-1">
                        <el-button
                            type="warning"
                            size="mini"
                            class="btn-block"
                            @click="handleRegisterIncomingNumbers(scope.row)"
                        >
                            Manage Numbers
                        </el-button>
                    </div>
                    <!-- Delete Button -->
                    <div class="mt-1">
                        <el-button
                            type="danger"
                            size="mini"
                            class="btn-block"
                            @click="handleDeleteBundle(scope.row)"
                            :loading="loadingDeleteBundle.includes(scope.row.id)"
                        >
                            Delete
                        </el-button>
                    </div>
                </template>
            </el-table-column>
        </el-table>
    </div>
</template>

<script>
import { compliance_mixin, acl_mixin } from '../../../mixins'
import InformationColumn from '../columns/information-column'
import TimestampsColumn from '../columns/timestamps-column'
import CountColumn from '../columns/count-column'
import StatusColumn from '../columns/status-column'
import FailureReasonColumn from '../columns/failure-reason-column'
import { TYPE_STANDARD, TYPE_ENHANCED, USE_CASE_CATEGORIES } from '../../../constants/branded-caller-id-use-cases'

export default {
    name: 'BrandedCallerIdBundleTable',
    components: {
        InformationColumn,
        TimestampsColumn,
        CountColumn,
        StatusColumn,
        FailureReasonColumn,
    },
    mixins: [compliance_mixin, acl_mixin],
    data() {
        return {
            TYPE_STANDARD,
            TYPE_ENHANCED,
        }
    },
    props: {
        loadingBundles: {
            type: Boolean,
            required: true,
        },
        company: {
            type: Object,
            required: true,
        },
        bundles: {
            type: Array,
            required: true,
        },
        loadingUpdateBundle: {
            type: Array,
            default: () => [],
        },
        loadingDeleteBundle: {
            type: Array,
            default: () => [],
        },
    },
    methods: {
        useCaseCategoryLabel(category) {
            const found = USE_CASE_CATEGORIES.find(item => item.value === category)
            return found?.label || category || '-'
        },
        handleViewDetails(bundle) {
            this.$emit('view-details', bundle)
        },
        handleRegisterIncomingNumbers(bundle) {
            this.$emit('register-incoming-numbers', bundle)
        },
        handleDeleteBundle(bundle) {
            this.$emit('delete-bundle', bundle)
        },
    },
}
</script>

<style scoped>
.type-badge {
    display: inline-block;
    padding: 2px 8px;
    border-radius: 10px;
    font-size: 12px;
    font-weight: 600;
    color: #fff;
}

.badge-enhanced {
    background-color: #409eff;
}

.badge-standard {
    background-color: #909399;
}
</style>
